import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Pencil, Trash2, FileText } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { RcsDeleteTemplateDialog } from './RcsDeleteTemplateDialog';
import { useRcsTemplatesList } from '@/hooks/useRcs';
import type { RcsTemplate, RcsTemplateType } from '@/types/api.types';

interface Props {
  onEdit: (template: RcsTemplate) => void;
}

const typeLabels: Record<RcsTemplateType, string> = {
  text: 'Texto',
  template: 'Template',
};

const typeVariants: Record<RcsTemplateType, 'default' | 'secondary'> = {
  text: 'default',
  template: 'secondary',
};

function RcsTemplateListSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
}

export function RcsTemplateList({ onEdit }: Props) {
  const { data, isLoading } = useRcsTemplatesList();
  const templates = data?.data ?? [];
  const [deleteTarget, setDeleteTarget] = useState<RcsTemplate | null>(null);

  if (isLoading) return <RcsTemplateListSkeleton />;

  if (templates.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="Nenhum template RCS cadastrado"
        description="Crie templates de mensagem RCS para usar em suas campanhas."
      />
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Criado em</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="font-medium">{t.name}</TableCell>
              <TableCell>
                <Badge variant={typeVariants[t.type]}>{typeLabels[t.type]}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant={t.isActive ? 'default' : 'secondary'}>
                  {t.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {format(new Date(t.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(t)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {deleteTarget && (
        <RcsDeleteTemplateDialog
          templateId={deleteTarget.id}
          name={deleteTarget.name}
          open={!!deleteTarget}
          onOpenChange={(o) => {
            if (!o) setDeleteTarget(null);
          }}
        />
      )}
    </>
  );
}
